import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './CreateStockEntryModal.css';
import CreateSupplierModal from '../Suppliers/CreateSupplierModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaTimes,
  FaBoxes,
  FaBuilding,
  FaPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaUser
} from 'react-icons/fa';

const RESTRICTED_CHAR_REGEX = /[,\\'"]/;

const INITIAL_FORM = {
  supplierId: '',
  supplierName: '',
  invoiceNumber: '',
  invoiceDate: new Date().toISOString().split('T')[0],
  representativeName: ''
};

const CreateStockEntryModal = ({
  isOpen,
  onClose,
  onSave,
  onSuccess,
  initialData = null,
  entryToEdit = null,
  suppliers = [],
  onAddNewSupplierSuccess
}) => {
  const targetEdit = initialData || entryToEdit;

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [supplierSearchText, setSupplierSearchText] = useState('');
  const [selectedSupplierObj, setSelectedSupplierObj] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  
  const [errorMessage, setErrorMessage] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State to control nested Add New Supplier modal
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Filter list to include ONLY ACTIVE suppliers (or status undefined/defaulting active)
  const activeSuppliers = useMemo(() => {
    return suppliers.filter((s) => {
      if (!s.status) return true;
      return String(s.status).toUpperCase() === 'ACTIVE';
    });
  }, [suppliers]);

  useEffect(() => {
    if (targetEdit) {
      setFormData({
        id: targetEdit.id,
        supplierId: targetEdit.supplierId || '',
        supplierName: targetEdit.supplierName || '',
        invoiceNumber: targetEdit.invoiceNumber || '',
        invoiceDate: targetEdit.invoiceDate || new Date().toISOString().split('T')[0],
        representativeName: targetEdit.representativeName || targetEdit.repName || ''
      });
      setSupplierSearchText(targetEdit.supplierName || '');
      const existing = activeSuppliers.find(
        (s) => String(s.id) === String(targetEdit.supplierId) || s.name.toLowerCase() === (targetEdit.supplierName || '').toLowerCase()
      );
      setSelectedSupplierObj(existing || null);
    } else {
      setFormData(INITIAL_FORM);
      setSupplierSearchText('');
      setSelectedSupplierObj(null);
    }
    setErrorMessage('');
    setToastMessage('');
    setFieldErrors({});
    setShowSuggestions(false);
  }, [targetEdit, isOpen, activeSuppliers]);

  // Click outside to close supplier suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleSupplierTextChange = (e) => {
    const text = e.target.value;
    setSupplierSearchText(text);
    
    // Check if typed text matches an exact ACTIVE supplier
    const exactMatch = activeSuppliers.find(
      (s) => s.name.toLowerCase() === text.trim().toLowerCase()
    );

    if (exactMatch) {
      setSelectedSupplierObj(exactMatch);
      setFormData((prev) => ({
        ...prev,
        supplierId: String(exactMatch.id),
        supplierName: exactMatch.name
      }));
    } else {
      setSelectedSupplierObj(null);
      setFormData((prev) => ({
        ...prev,
        supplierId: '',
        supplierName: text
      }));
    }

    if (text.trim().length > 0) {
      const matches = activeSuppliers.filter((s) =>
        s.name.toLowerCase().includes(text.toLowerCase())
      );
      setFilteredSuppliers(matches);
      setShowSuggestions(true);
    } else {
      setFilteredSuppliers(activeSuppliers);
      setShowSuggestions(false);
    }
  };

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplierObj(supplier);
    setFormData((prev) => ({
      ...prev,
      supplierId: String(supplier.id),
      supplierName: supplier.name
    }));
    setSupplierSearchText(supplier.name);
    setShowSuggestions(false);
    setErrorMessage('');
  };

  const handleInputChange = (field, value) => {
    // Restricted character validation
    if (['invoiceNumber', 'representativeName'].includes(field)) {
      if (RESTRICTED_CHAR_REGEX.test(value)) {
        setFieldErrors((prev) => ({
          ...prev,
          [field]: 'Note: special characters comma, double quotes and single quotes are not allowed'
        }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Called when saving a newly created supplier from the nested CreateSupplierModal.
   * Calls the backend API to persist the supplier to DB, updates supplier state,
   * shows success toast, and auto-selects the new supplier.
   */
  const handleCreateSupplierSave = async (supplierFormData) => {
    try {
      let createdSupplier = null;
      if (ENDPOINTS.ADD_SUPPLIER) {
        const res = await axios.post(ENDPOINTS.ADD_SUPPLIER, {
          ...supplierFormData,
          status: 'ACTIVE'
        });
        createdSupplier = res.data || { ...supplierFormData, status: 'ACTIVE', id: Date.now() };
      } else {
        createdSupplier = { ...supplierFormData, status: 'ACTIVE', id: Date.now() };
      }

      // Update parent suppliers list
      if (onAddNewSupplierSuccess) {
        onAddNewSupplierSuccess(createdSupplier);
      }

      // Auto select newly saved supplier in Purchase Order form
      setSelectedSupplierObj(createdSupplier);
      setFormData((prev) => ({
        ...prev,
        supplierId: String(createdSupplier.id),
        supplierName: createdSupplier.name
      }));
      setSupplierSearchText(createdSupplier.name);
      setIsAddSupplierOpen(false);
      setErrorMessage('');
      
      // Trigger toast message
      setToastMessage(`Supplier "${createdSupplier.name}" saved successfully to database!`);
      setTimeout(() => setToastMessage(''), 4500);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save new supplier to database.';
      toast.error(`Supplier creation error: ${msg}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Must be selected from existing active data or created new via modal
    if (!supplierSearchText.trim()) {
      setErrorMessage('Supplier Name is required.');
      return;
    }

    // Verify supplier is validated against existing active supplier objects
    const verifiedSupplier = selectedSupplierObj || activeSuppliers.find(
      (s) => s.name.toLowerCase() === supplierSearchText.trim().toLowerCase()
    );

    if (!verifiedSupplier) {
      setErrorMessage('Invalid supplier name. Please select an ACTIVE supplier from the suggestions or click "Add New Supplier" to create one.');
      return;
    }

    // Backend / Status Check: verify status is ACTIVE
    const isSupplierActive = !verifiedSupplier.status || String(verifiedSupplier.status).toUpperCase() === 'ACTIVE';
    if (!isSupplierActive) {
      setErrorMessage(`Supplier "${verifiedSupplier.name}" is currently INACTIVE. Only ACTIVE suppliers can be selected for purchase orders.`);
      return;
    }

    if (!formData.invoiceNumber.trim()) {
      setErrorMessage('Invoice No is required.');
      return;
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrorMessage('Please resolve field warnings before saving.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        supplierId: Number(verifiedSupplier.id),
        supplierName: verifiedSupplier.name,
        entryType: 'PURCHASE_ORDER',
        status: 'INVOICED',
        amount: 0.00,
        clearedAmount: 0.00,
        entryDate: new Date().toISOString().split('T')[0]
      };

      let resultEntry = null;
      if (targetEdit && targetEdit.id) {
        const res = await axios.put(ENDPOINTS.UPDATE_PURCHASE_ENTRY(targetEdit.id), payload);
        resultEntry = res.data;
      } else {
        const res = await axios.post(ENDPOINTS.CREATE_PURCHASE_ENTRY, payload);
        resultEntry = res.data;
      }

      if (onSave) {
        await onSave(resultEntry || payload);
      }
      if (onSuccess) {
        await onSuccess(resultEntry || payload);
      }
      onClose();
    } catch (err) {
      const errDetail = err.response?.data?.error || err.message || 'Failed to save purchase entry to database.';
      setErrorMessage(errDetail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <>
      <div className="stock-modal-backdrop" onClick={onClose}>
        <div className="stock-modal-card" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="stock-modal-header">
            <div className="stock-modal-title-row">
              <div className="stock-modal-icon-badge">
                <FaBoxes />
              </div>
              <div>
                <h3 className="stock-modal-title">
                  {targetEdit ? 'Edit Purchase Order' : 'Create Purchase Order'}
                </h3>
                <p className="stock-modal-subtitle">
                  Select active supplier, enter invoice details and representative name
                </p>
              </div>
            </div>
            <button
              type="button"
              className="stock-modal-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="stock-modal-form">
            {toastMessage && (
              <div className="stock-modal-alert success">
                <FaCheckCircle />
                <span>{toastMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="stock-modal-alert danger">
                <FaExclamationTriangle />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="stock-modal-body">
              <div className="stock-form-grid">
                {/* 1. Supplier Name with Auto-suggestions & Add New Supplier Button Beside */}
                <div className="stock-form-group stock-form-full">
                  <label className="stock-form-label">
                    <FaBuilding className="field-icon" /> Supplier Name <span className="req">*</span>
                  </label>
                  <div className="supplier-input-row" ref={dropdownRef}>
                    <div className="supplier-autocomplete-wrapper">
                      <input
                        type="text"
                        className={`stock-form-input ${!selectedSupplierObj && supplierSearchText ? 'is-unverified' : ''}`}
                        placeholder="Type supplier name..."
                        value={supplierSearchText}
                        onChange={handleSupplierTextChange}
                        onFocus={() => {
                          setFilteredSuppliers(
                            supplierSearchText.trim()
                              ? activeSuppliers.filter((s) =>
                                  s.name.toLowerCase().includes(supplierSearchText.toLowerCase())
                                )
                              : activeSuppliers
                          );
                          setShowSuggestions(true);
                        }}
                        required
                      />
                      {showSuggestions && (
                        <ul className="supplier-suggestions-list">
                          {filteredSuppliers.length > 0 ? (
                            filteredSuppliers.map((s) => (
                              <li
                                key={s.id}
                                className="supplier-suggestion-item"
                                onClick={() => handleSelectSupplier(s)}
                              >
                                <span className="supplier-suggestion-name">{s.name}</span>
                                {s.gstNumber && (
                                  <span className="supplier-suggestion-meta">GST: {s.gstNumber}</span>
                                )}
                              </li>
                            ))
                          ) : (
                            <li className="supplier-suggestion-empty">
                              No matching supplier found. Click &quot;Add New Supplier&quot; beside to create one.
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-add-new-supplier"
                      onClick={() => setIsAddSupplierOpen(true)}
                    >
                      <FaPlus size={12} /> Add New Supplier
                    </button>
                  </div>
                </div>

                {/* 2. Invoice No */}
                <div className="stock-form-group stock-form-full">
                  <label className="stock-form-label">
                    Invoice No <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`stock-form-input ${fieldErrors.invoiceNumber ? 'has-error' : ''}`}
                    placeholder="e.g. INV-2026-889"
                    value={formData.invoiceNumber}
                    onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                    required
                  />
                  <div className="stock-restriction-note">
                    Note: special characters comma, double quotes and single quotes are not allowed
                  </div>
                  {fieldErrors.invoiceNumber && (
                    <span className="stock-field-error">{fieldErrors.invoiceNumber}</span>
                  )}
                </div>

                {/* 3. Invoice Date */}
                <div className="stock-form-group">
                  <label className="stock-form-label">
                    Invoice Date <span className="req">*</span>
                  </label>
                  <input
                    type="date"
                    className="stock-form-input"
                    placeholder="Select date"
                    value={formData.invoiceDate}
                    onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                    required
                  />
                </div>

                {/* 4. Representative Name */}
                <div className="stock-form-group">
                  <label className="stock-form-label">
                    <FaUser className="field-icon" /> Representative Name
                  </label>
                  <input
                    type="text"
                    className={`stock-form-input ${fieldErrors.representativeName ? 'has-error' : ''}`}
                    placeholder="e.g. John Doe (Sales Rep)"
                    value={formData.representativeName}
                    onChange={(e) => handleInputChange('representativeName', e.target.value)}
                  />
                  {fieldErrors.representativeName && (
                    <span className="stock-field-error">{fieldErrors.representativeName}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="stock-modal-footer">
              <button
                type="button"
                className="stock-btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="stock-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Saving...'
                  : targetEdit
                  ? 'Update Purchase Order'
                  : 'Create Purchase Order'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Embedded CreateSupplierModal for Add New Supplier button */}
      {isAddSupplierOpen && (
        <CreateSupplierModal
          isOpen={isAddSupplierOpen}
          onClose={() => setIsAddSupplierOpen(false)}
          onSave={handleCreateSupplierSave}
        />
      )}
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CreateStockEntryModal;
